import * as R from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { Wrapper, Title } from './Styled';
import { MetaDataOption, MetaDataOptionProps } from './MetaDataOption';
import { CycleMetaDataValue } from 'types/api/envScales/CycleMetaDataValue';
import { MetaDataCategory } from 'types/api/envScales/MetaDataCategory';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { ContentAndFormat1stEditionError } from 'types/api/envScales/CyclesErrors';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import {
  getInitialValues,
  getPayloadValues,
  SelectedValues,
  showMetaDataCategoryErrors
} from './utils';
import { ErrorMessage } from 'components/Measure/View/Styled';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { useCyclesErrors } from 'context/CyclesErrorsContext';
import { MetaDataCheckBox } from './MetaDataCheckBox';

interface MetaDataRatingProps {
  /** Observation ID. */
  observationId: number;

  /** Cycle */
  cycle: ObservationCycle;

  /** Initial meta data values. */
  values: CycleMetaDataValue[];

  /** Meta data categories. */
  categories: MetaDataCategory[];
}

function MetaDataRating({
  observationId,
  cycle,
  values: initialValues,
  categories
}: MetaDataRatingProps) {
  const { cyclesErrors, updateCycleErrors } = useCyclesErrors();
  const [touched, setTouched] = useState(false);
  const [formatTouched, setFormatTouched] = useState(false);
  const contentCategories = categories.find(
    category => category.t_name === 'content'
  );
  const formatCategories = categories.find(
    category => category.t_name === 'format'
  );
  const [values, setValues] = useState(
    getInitialValues(contentCategories!, initialValues)
  );
  const [formatValues, setFormatValues] = useState(
    getInitialValues(formatCategories!, initialValues)
  );

  const contentAreas: any = categories
    .filter(category => category.t_name === 'content')
    .map(content => ({
      id: content.id,
      name: content.name,
      description: content.description,
      choices: content.choices
    }));

  const contentFormats: any = categories
    .filter(category => category.t_name === 'format')
    .map(format => ({
      id: format.id,
      name: format.name,
      description: format.description,
      choices: format.choices
    }));

  const handleDebouncedUpdate = (
    touched: boolean,
    values: SelectedValues,
    categoryId: number
  ) => {
    if (!touched) {
      return;
    }

    const payload = {
      meta_data: {
        meta_data_category_id: categoryId,
        values: getPayloadValues(values)
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload);
  };

  useDebouncedEffect(
    () =>
      contentCategories &&
      handleDebouncedUpdate(touched, values, contentCategories.id),
    1000,
    [values, touched]
  );

  useDebouncedEffect(
    () =>
      formatCategories &&
      handleDebouncedUpdate(formatTouched, formatValues, formatCategories.id),
    1000,
    [formatValues, formatTouched]
  );

  const handleMetaDataChange = (
    data: { name: string; value: { isPrimary: boolean; [key: string]: any } },
    currentValues: SelectedValues,
    setValuesFn: React.Dispatch<React.SetStateAction<SelectedValues>>,
    setTouchedFn: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    let newValues = { ...currentValues };
    const isNewPrimary = data.value.isPrimary;

    if (isNewPrimary) {
      newValues = R.mapObjIndexed(
        value => ({ ...value, isPrimary: false }),
        newValues
      );
    }

    const mergedValues = {
      ...newValues,
      [data.name]: {
        ...data.value,
        isChecked: data.value.isChecked ?? false
      }
    };
    setValuesFn(mergedValues);
    validate(mergedValues);
    setTouchedFn(true);
  };

  const handleChange: MetaDataOptionProps['onChange'] = data => {
    handleMetaDataChange(data, values, setValues, setTouched);
  };

  const handleFormatChange: MetaDataOptionProps['onChange'] = data => {
    handleMetaDataChange(data, formatValues, setFormatValues, setFormatTouched);
  };

  function validate(values: SelectedValues) {
    if (R.isEmpty(cyclesErrors) || !contentCategories) {
      return;
    }

    const categoryChecked = Object.keys(values)
      .map(categoryName => values[categoryName].isChecked)
      .some(checked => checked);
    const categoryPrimaryChecked = Object.keys(values)
      .map(categoryName => values[categoryName].isPrimary)
      .some(checked => checked);

    let newCyclesErrors = R.clone(cyclesErrors);

    let checkedError = null;
    if (!categoryChecked) {
      checkedError = i18n.ft(messages.errors.categoryError, {
        categoryName: contentCategories.t_name
      });
    }
    (
      newCyclesErrors[cycle.cycle_number]
        .meta_data_categories as ContentAndFormat1stEditionError
    )[contentCategories.t_name].error = checkedError;

    let primaryChecked = null;
    if (!categoryPrimaryChecked) {
      primaryChecked = i18n.ft(messages.errors.primaryError, {
        categoryName: contentCategories.t_name
      });
    }
    (
      newCyclesErrors[cycle.cycle_number]
        .meta_data_categories as ContentAndFormat1stEditionError
    )[contentCategories.t_name].primary_error = primaryChecked;

    updateCycleErrors(newCyclesErrors);
  }

  function verifyCategoryData() {
    if (
      R.isEmpty(categories) ||
      categories.length < 2 ||
      contentCategories?.t_name !== 'content' ||
      formatCategories?.t_name !== 'format' ||
      contentCategories?.choices?.length === 0 ||
      formatCategories?.choices?.length === 0
    ) {
      return true;
    }

    return false;
  }

  if (verifyCategoryData()) {
    return null;
  }

  return (
    <Wrapper>
      {showMetaDataCategoryErrors(
        cyclesErrors,
        contentCategories?.t_name || '',
        cycle.cycle_number
      ) && (
        <ErrorMessage>
          {showMetaDataCategoryErrors(
            cyclesErrors,
            contentCategories?.t_name || '',
            cycle.cycle_number
          )}
        </ErrorMessage>
      )}
      <div className="grid grid-cols-1 md:grid-cols-12 md:gap-4">
        <div className="col-span-5">
          {contentAreas.map((contentArea: any) => (
            <div key={contentArea.id} className="mb-[24px]">
              <Title>
                {i18n.ft(messages.type, { category: contentArea.name })}
              </Title>
              <div className="mb-[32px]">{contentArea.description}</div>

              {contentArea.choices.map((choice: any) => (
                <MetaDataOption
                  key={`${contentArea.id}-${choice.id}`}
                  choice={choice}
                  value={values[choice.t_name]}
                  onChange={handleChange}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="col-span-2 w-[10px] rounded-[10px] bg-[#f4f8fa] mx-auto"></div>

        <div className="col-span-5">
          {contentFormats.map((contentFormat: any) => (
            <div key={contentFormat.id} className="mb-[24px]">
              <Title>
                {i18n.ft(messages.type, { category: contentFormat.name })}
              </Title>
              <div className="mb-[32px]">{contentFormat.description}</div>

              {contentFormat.choices.map((choice: any) => (
                <MetaDataCheckBox
                  key={`${contentFormat.id}-${choice.id}`}
                  choice={choice}
                  value={formatValues[choice.t_name]}
                  onChange={handleFormatChange}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}

export default MetaDataRating;
